import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SimpleApiResponse } from '../models/command-responses/simple-api-response.model';
import { UpdateNetworkCommandResponse } from '../models/command-responses/update-network-command-response.model';
import { GatewayDiscover } from '../models/gateway/gateway-discover.model';
import { GatewayNetworkConfigurationValue } from '../models/gateway/gateway-network-configuration-value.model';
import { GatewayNetworkConfiguration } from '../models/gateway/gateway-network-configuration.model';
import { ToroDeviceUpdateResponse } from '../models/toro-device-update-response.model';
import { GatewayApiService } from '../services/gateway-api.service';

@Injectable()
export class GatewayManagerService
{
    private _gatewayNetworkConfigurations: { [gatewayId: number]: GatewayNetworkConfiguration; } = {};

    constructor(private gatewayApiService: GatewayApiService)
    {
    }

    public createGateway(accountId: number, locationId: number, uid: string, pin: number): Observable<ToroDeviceUpdateResponse>
    {
        return this.gatewayApiService.createGateway(accountId, locationId, uid, pin);
    }

    public updateGateway(gatewayId: number, locationId: number, uid: string, pin: number): Observable<ToroDeviceUpdateResponse>
    {
        return this.gatewayApiService.updateGateway(gatewayId, locationId, uid, pin);
    }

    public deleteGateway(gatewayId: number): Observable<ToroDeviceUpdateResponse>
    {
        return this.gatewayApiService.deleteGateway(gatewayId);
    }

    public resyncGateway(gatewayId: number): Observable<ToroDeviceUpdateResponse>
    {
        return this.gatewayApiService.resyncGateway(gatewayId);
    }

    public putGatewayInDiscoveryMode(gatewayId: number, accountId: number, locationId: number): Observable<GatewayDiscover>
    {
        return this.gatewayApiService.putGatewayInDiscoveryMode(gatewayId, accountId, locationId);
    }

    public cancelGatewayDiscoveryMode(gatewayId: number)
    {
        return this.gatewayApiService.cancelGatewayDiscoveryMode(gatewayId);
    }

    public discoveryGateway(replace: boolean, accountId: number, gatewayDiscover: GatewayDiscover): Observable<any>
    {
        return this.gatewayApiService.discoverGateway(replace, accountId, gatewayDiscover);
    }

    public getGatewayNetworkConfiguration(gatewayId: number, refresh: boolean = false, cached: boolean = true): Observable<GatewayNetworkConfiguration | SimpleApiResponse>
    {
        if (cached && !refresh && this._gatewayNetworkConfigurations[gatewayId])
        {
            return of(this._gatewayNetworkConfigurations[gatewayId]);
        }

        return this.gatewayApiService.getGatewayNetworkConfiguration(gatewayId, refresh)
            .pipe(
                tap((response: GatewayNetworkConfiguration | SimpleApiResponse) =>
                {
                    if (!(response instanceof GatewayNetworkConfiguration)) return;

                    this._gatewayNetworkConfigurations[gatewayId] = response;
                })
            );
    }

    public updateGatewayNetworkConfiguration(gatewayId: number, networkConfigurationValue: GatewayNetworkConfigurationValue): Observable<UpdateNetworkCommandResponse>
    {
        return this.gatewayApiService.updateGatewayNetworkConfiguration(gatewayId, networkConfigurationValue);
    }

    public wakeupGateway(gatewayId: number)
    {
        return this.gatewayApiService.wakeupGateway(gatewayId);
    }
}
